import React, { FC, useEffect } from 'react';
import moment from 'moment-timezone';

import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { TranslationKey, useTranslations } from '@lang';
import { isTimestampsFromSameDay, parseDateToString } from '@utils/helpers';
import { gridTheme } from '@utils/grid';
import { UnavailableBox } from '@common/components';
import { usePageContext } from '@common/components/PageContext';
import { DatesSlider } from '@common/components/DatesSlider';
import { StartDatePicker } from '@common/components/StartDatePicker';
import { Heading } from '@common/components/BookingForms';
import { FormBox, FormWrapper } from './Availability.styles';
import { AvailabilityProps } from './Availability.types';
import { TimeSlotSelector } from './TimeSlotSelector';
import { SpaEntityFormValues } from '@common/components/SpaEntity/SpaEntityForm.schema';

import {
  getAvailability,
  getSliderDates,
  startDateAfterChange,
  visitDayAfterChange,
} from './helpers';
import { useIsWindowWiderThan } from '@common/hooks';
import { EntityTypes } from '@common-types/Entities';
import { DenialReason } from '@features/analytics/utilities/analytics.types';
import { useDenialTracking } from '@features/analytics/hooks/useDenialTracking';
import { useGetSpaSettings } from '../spa/hooks';
import { changeJourneyParam } from '@store/yourJourney/actions';
import { useDispatch } from 'react-redux';

export const Availability: FC<AvailabilityProps> = ({
  fetchAvailability,
  allTimeslots,
  isModalOpen,
  spaEntity,
  maxDaysAdvance = 0,
  reservedSpaTimeSlots = [],
  initialValues,
  entityType,
  fetchingDates,
  property,
  setStartDate,
}) => {
  const { control, setValue } = useFormContext<SpaEntityFormValues>();
  const dispatch = useDispatch();
  const visitDate = useWatch({ control, name: 'visitDate' });
  const startDate = useWatch({ control, name: 'startDate' });

  const dispatchChangeJourneyParam = (key: string, value: any) => {
    dispatch(changeJourneyParam(key, value));
  };

  useEffect(() => {
    getAvailability(initialValues.startDate, fetchAvailability, setValue, entityType);
  }, []);

  const { data: spaSettings } = useGetSpaSettings(property.id);
  const { phoneNumber } = spaSettings || {};

  const { t } = useTranslations();
  const { parentPageName } = usePageContext();
  const { telephoneOnly } = spaEntity;

  const isDesktop = useIsWindowWiderThan(gridTheme.breakpoints.md);
  const availableTimeOptions =
    (allTimeslots?.length > 0 &&
      allTimeslots.filter(
        (time) => !reservedSpaTimeSlots.includes(time.startDateTime as string)
      )) ||
    [];

  const arrivalTimesOptions =
    availableTimeOptions
      .filter((option) => isTimestampsFromSameDay(option.startDateTime, visitDate))
      .map((option) => ({
        ...option,
        startDateTime: option.startDateTime,
        value: option.startDateTime,
      })) || [];

  const noAvailableTimesForStartDate = Boolean(
    allTimeslots.length &&
      !fetchingDates?.length &&
      !availableTimeOptions.filter((option) =>
        isTimestampsFromSameDay(option.startDateTime, startDate)
      ).length
  );

  useDenialTracking({
    denied: noAvailableTimesForStartDate,
    reason: DenialReason.NO_AVAILABILITY,
    itemCategory: entityType === EntityTypes.TREATMENT ? 'spa-treatments' : 'spa-facilities',
    itemId: `${spaEntity.id}`,
    itemName: spaEntity.name,
    startDate: moment(startDate).format('YYYY-MM-DD'),
  });

  const timeslotLabel: TranslationKey =
    entityType === EntityTypes.TREATMENT ? 'treatment.selectArrival' : 'spa.selectArrival';

  return (
    <FormWrapper>
      <Heading>{t('treatment.availabilityStepHeader')}</Heading>
      <FormBox>
        <Controller
          control={control}
          name="startDate"
          render={({ field: { value, onChange } }) => (
            <StartDatePicker
              input={{ value, onChange }}
              handleAfterChange={(sDate) => {
                startDateAfterChange(
                  sDate,
                  setValue,
                  dispatchChangeJourneyParam,
                  fetchAvailability,
                  entityType
                );
                setStartDate && setStartDate(parseDateToString(sDate));
              }}
              pickerTextId="common.calendar.changeDateCTA"
              popperModifiers={{
                preventOverflow: {
                  enabled: true,
                  escapeWithReference: false,
                  boundariesElement: 'viewport',
                },
              }}
              maxDate={maxDaysAdvance ? moment().add(maxDaysAdvance, 'days') : undefined}
            />
          )}
        />
        {isModalOpen && (
          <Controller
            control={control}
            name="visitDate"
            render={({ field: { value, onChange } }) => (
              <DatesSlider
                key={startDate && startDate.format ? startDate.format('YYYY-MM-DD') : startDate}
                input={{ value, onChange }}
                handleAfterChange={(date) =>
                  visitDayAfterChange(date, setValue, dispatchChangeJourneyParam)
                }
                dates={getSliderDates(
                  allTimeslots,
                  startDate
                    ? startDate.format
                      ? startDate.format()
                      : startDate
                    : moment().format()
                )}
                hasPrices
                visitDate={visitDate}
                loadingDates={fetchingDates}
                telephoneOnly={spaEntity.telephoneOnly}
                isLoading={!!fetchingDates?.length}
              />
            )}
          />
        )}
        <TimeSlotSelector
          arrivalTimesOptions={arrivalTimesOptions}
          disabled={!!fetchingDates && fetchingDates.length > 0}
          labelTranslation={timeslotLabel}
          showPerPerson={entityType === EntityTypes.FACILITY}
        />
      </FormBox>
      {telephoneOnly && (
        <UnavailableBox
          title={t('spa.telephoneOnly', { spaEntity: parentPageName })}
          body={t('spa.callUsOn', {
            phoneNumber: isDesktop ? (
              phoneNumber
            ) : (
              <a href={`tel: ${phoneNumber}`}>{phoneNumber}</a>
            ),
          })}
        />
      )}
    </FormWrapper>
  );
};
